import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { ROUTER_ADDRESS, PUZO_ADDRESS, USDT_ADDRESS } from '../config/constants';
import { useBlockchainData } from '../hooks/useBlockchainData';
import ROUTER_ABI from '../abis/router.json';
import ERC20_ABI from '../abis/erc20.json';

const Liquidity: React.FC = () => {
  const { provider, signer, account, isConnected, connectWallet } = useWeb3();
  const { puzoUsdtPool } = useBlockchainData(provider);
  
  const [puzoAmount, setPuzoAmount] = useState('');
  const [usdtAmount, setUsdtAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [puzoBalance, setPuzoBalance] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [puzoApproved, setPuzoApproved] = useState(false);
  const [usdtApproved, setUsdtApproved] = useState(false);
  const [lpBalance, setLpBalance] = useState('0');
  const [activeTab, setActiveTab] = useState('add');

  useEffect(() => {
    const loadBalances = async () => {
      if (!provider || !account) return;

      try {
        const puzoContract = new ethers.Contract(PUZO_ADDRESS, ERC20_ABI, provider);
        const usdtContract = new ethers.Contract(USDT_ADDRESS, ERC20_ABI, provider);

        const [puzoBal, usdtBal, puzoAllowance, usdtAllowance] = await Promise.all([
          puzoContract.balanceOf(account),
          usdtContract.balanceOf(account),
          puzoContract.allowance(account, ROUTER_ADDRESS),
          usdtContract.allowance(account, ROUTER_ADDRESS)
        ]);

        setPuzoBalance(ethers.utils.formatEther(puzoBal));
        setUsdtBalance(ethers.utils.formatEther(usdtBal));
        
        const neededAmount = ethers.utils.parseEther('1000');
        setPuzoApproved(puzoAllowance.gte(neededAmount));
        setUsdtApproved(usdtAllowance.gte(neededAmount));

        if (puzoUsdtPool) {
          const pairContract = new ethers.Contract(puzoUsdtPool.address, ERC20_ABI, provider);
          const lpBal = await pairContract.balanceOf(account);
          setLpBalance(ethers.utils.formatEther(lpBal));
        }

      } catch (error) {
        console.error('Error loading balances:', error);
      }
    };

    loadBalances();
  }, [provider, account, puzoUsdtPool]);

  // Auto-calculate USDT based on pool ratio
  useEffect(() => {
    if (puzoAmount && puzoUsdtPool && parseFloat(puzoUsdtPool.reserve0) > 0) {
      const price = parseFloat(puzoUsdtPool.reserve1) / parseFloat(puzoUsdtPool.reserve0);
      const calculatedUsdt = (parseFloat(puzoAmount) * price).toFixed(6);
      setUsdtAmount(calculatedUsdt);
    }
  }, [puzoAmount, puzoUsdtPool]);

  const approveToken = async (tokenAddress: string): Promise<boolean> => {
    if (!signer) return false;

    try {
      setLoading(true);
      setError('');

      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const tx = await tokenContract.approve(ROUTER_ADDRESS, ethers.constants.MaxUint256);
      await tx.wait();

      if (tokenAddress === PUZO_ADDRESS) setPuzoApproved(true);
      if (tokenAddress === USDT_ADDRESS) setUsdtApproved(true);
      
      return true;
    } catch (error: any) {
      setError('Approve failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddLiquidity = async () => {
    if (!isConnected || !signer || !account) {
      await connectWallet();
      return;
    }

    if (!puzoAmount || !usdtAmount || parseFloat(puzoAmount) <= 0 || parseFloat(usdtAmount) <= 0) {
      setError('Please enter valid amounts');
      return;
    }

    if (parseFloat(puzoAmount) > parseFloat(puzoBalance)) {
      setError('Insufficient PUZO balance');
      return;
    }

    if (parseFloat(usdtAmount) > parseFloat(usdtBalance)) {
      setError('Insufficient USDT balance');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, ROUTER_ABI, signer);

      const puzoAmountWei = ethers.utils.parseEther(puzoAmount);
      const usdtAmountWei = ethers.utils.parseEther(usdtAmount);
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

      if (!puzoApproved) {
        const success = await approveToken(PUZO_ADDRESS);
        if (!success) return;
      }

      if (!usdtApproved) {
        const success = await approveToken(USDT_ADDRESS);
        if (!success) return;
      }

      const puzoMin = puzoAmountWei.mul(99).div(100);
      const usdtMin = usdtAmountWei.mul(99).div(100);

      const tx = await routerContract.addLiquidity(
        PUZO_ADDRESS,
        USDT_ADDRESS,
        puzoAmountWei,
        usdtAmountWei,
        puzoMin,
        usdtMin,
        account,
        deadline,
        { gasLimit: 500000 }
      );

      await tx.wait();
      setPuzoAmount('');
      setUsdtAmount('');
      setError('Liquidity added successfully');

    } catch (error: any) {
      setError('Add liquidity failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!isConnected || !signer || !account || !puzoUsdtPool) return;

    setLoading(true);
    setError('');

    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, ROUTER_ABI, signer);
      const pairContract = new ethers.Contract(puzoUsdtPool.address, ERC20_ABI, signer);

      const lpAllowance = await pairContract.allowance(account, ROUTER_ADDRESS);
      if (lpAllowance.eq(0)) {
        const approveTx = await pairContract.approve(ROUTER_ADDRESS, ethers.constants.MaxUint256);
        await approveTx.wait();
      }

      const lpAmount = await pairContract.balanceOf(account);
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

      const tx = await routerContract.removeLiquidity(
        PUZO_ADDRESS,
        USDT_ADDRESS,
        lpAmount,
        0,
        0,
        account,
        deadline,
        { gasLimit: 500000 }
      );

      await tx.wait();
      setError('Liquidity removed successfully');

    } catch (error: any) {
      setError('Remove liquidity failed');
    } finally {
      setLoading(false);
    }
  };

  const formatInput = (value: string) => {
    return value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
  };

  const formatBalance = (balance: string) => {
    return parseFloat(balance).toLocaleString('en-US', {
      maximumFractionDigits: 6
    });
  };

  const canAddLiquidity = isConnected && 
    puzoAmount && 
    usdtAmount && 
    parseFloat(puzoAmount) > 0 && 
    parseFloat(usdtAmount) > 0 &&
    parseFloat(puzoAmount) <= parseFloat(puzoBalance) &&
    parseFloat(usdtAmount) <= parseFloat(usdtBalance) &&
    puzoApproved && 
    usdtApproved;

  return (
    <div>
      {error && (
        <div className={`system-alert ${error.includes('successfully') ? 'success' : 'error'}`}>
          {error}
        </div>
      )}

      {puzoUsdtPool && (
        <div className="interface-panel mb-20">
          <div className="panel-header">
            <div className="panel-title">Pool Statistics</div>
          </div>
          <div className="panel-content">
            <div className="quantum-stats">
              <div className="quantum-stat">
                <div className="stat-label">PUZO Reserve</div>
                <div className="stat-value">{formatBalance(puzoUsdtPool.reserve0)}</div>
              </div>
              <div className="quantum-stat">
                <div className="stat-label">USDT Reserve</div>
                <div className="stat-value">{formatBalance(puzoUsdtPool.reserve1)}</div>
              </div>
              <div className="quantum-stat">
                <div className="stat-label">Total LP</div>
                <div className="stat-value">{formatBalance(puzoUsdtPool.totalSupply)}</div>
              </div>
              {isConnected && (
                <div className="quantum-stat">
                  <div className="stat-label">Your LP</div>
                  <div className="stat-value">{formatBalance(lpBalance)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="neural-nav mb-20">
        <button 
          className={`neural-tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          Add
        </button>
        <button 
          className={`neural-tab ${activeTab === 'remove' ? 'active' : ''}`}
          onClick={() => setActiveTab('remove')}
          disabled={!puzoUsdtPool || parseFloat(lpBalance) === 0}
        >
          Remove
        </button>
      </div>

      {isConnected && (
        <div className="interface-panel mb-20">
          <div className="panel-header">
            <div className="panel-title">Your Balances</div>
          </div>
          <div className="panel-content">
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '0.8rem'}}>
              <div>
                <div style={{color: '#888', marginBottom: '4px'}}>PUZO</div>
                <div>{formatBalance(puzoBalance)}</div>
              </div>
              <div>
                <div style={{color: '#888', marginBottom: '4px'}}>USDT</div>
                <div>{formatBalance(usdtBalance)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'add' && (
        <div className="interface-panel">
          <div className="panel-header">
            <div className="panel-title">Add Liquidity</div>
          </div>
          <div className="panel-content">
            <div className="input-group">
              <div className="input-label">PUZO Amount</div>
              <input
                type="text"
                className="quantum-input"
                value={puzoAmount}
                onChange={(e) => setPuzoAmount(formatInput(e.target.value))}
                placeholder="0.0"
                disabled={loading}
              />
              {isConnected && (
                <div style={{fontSize: '0.7rem', color: '#888', marginTop: '4px'}}>
                  Balance: {formatBalance(puzoBalance)}
                </div>
              )}
            </div>

            <div className="input-group">
              <div className="input-label">USDT Amount</div>
              <input
                type="text"
                className="quantum-input"
                value={usdtAmount}
                readOnly
                placeholder="0.0"
              />
              {isConnected && (
                <div style={{fontSize: '0.7rem', color: '#888', marginTop: '4px'}}>
                  Balance: {formatBalance(usdtBalance)}
                </div>
              )}
            </div>

            {isConnected && (
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px'}}>
                <div style={{fontSize: '0.7rem', padding: '8px', border: '1px solid var(--border)', textAlign: 'center'}}>
                  PUZO: {puzoApproved ? 'APPROVED' : 'NOT APPROVED'}
                </div>
                <div style={{fontSize: '0.7rem', padding: '8px', border: '1px solid var(--border)', textAlign: 'center'}}>
                  USDT: {usdtApproved ? 'APPROVED' : 'NOT APPROVED'}
                </div>
              </div>
            )}

            {isConnected && (!puzoApproved || !usdtApproved) && (
              <button 
                className="quantum-btn mb-16"
                onClick={() => {
                  if (!puzoApproved) approveToken(PUZO_ADDRESS);
                  else if (!usdtApproved) approveToken(USDT_ADDRESS);
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="quantum-loading"></span>
                    APPROVING
                  </>
                ) : (
                  'APPROVE TOKENS'
                )}
              </button>
            )}

            {!isConnected ? (
              <button className="quantum-btn primary" onClick={connectWallet}>
                CONNECT WALLET
              </button>
            ) : (
              <button 
                className="quantum-btn primary" 
                onClick={handleAddLiquidity}
                disabled={loading || !canAddLiquidity}
              >
                {loading ? (
                  <>
                    <span className="quantum-loading"></span>
                    ADDING LIQUIDITY
                  </>
                ) : (
                  'ADD LIQUIDITY'
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === 'remove' && puzoUsdtPool && parseFloat(lpBalance) > 0 && (
        <div className="interface-panel">
          <div className="panel-header">
            <div className="panel-title">Remove Liquidity</div>
          </div>
          <div className="panel-content">
            <div style={{marginBottom: '20px'}}>
              <div style={{fontSize: '0.8rem', color: '#888', marginBottom: '8px'}}>Your LP Shares</div>
              <div style={{fontSize: '1.1rem', marginBottom: '16px'}}>{formatBalance(lpBalance)} LP</div>
              
              <div style={{fontSize: '0.8rem', color: '#888', marginBottom: '8px'}}>Estimated Return</div>
              <div style={{display: 'grid', gap: '8px', fontSize: '0.8rem'}}>
                <div>PUZO: {((parseFloat(lpBalance) / parseFloat(puzoUsdtPool.totalSupply)) * parseFloat(puzoUsdtPool.reserve0)).toFixed(6)}</div>
                <div>USDT: {((parseFloat(lpBalance) / parseFloat(puzoUsdtPool.totalSupply)) * parseFloat(puzoUsdtPool.reserve1)).toFixed(6)}</div>
              </div>
            </div>

            <button 
              className="quantum-btn"
              onClick={handleRemoveLiquidity}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="quantum-loading"></span>
                  REMOVING LIQUIDITY
                </>
              ) : (
                'REMOVE ALL LIQUIDITY'
              )}
            </button>
          </div>
        </div>
      )}

      {!isConnected && activeTab === 'remove' && (
        <button className="quantum-btn primary" onClick={connectWallet}>
          CONNECT WALLET
        </button>
      )}
    </div>
  );
};

export default Liquidity;
